import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { opcionesDeporte } from '../../../lib/deportesOrg'
import { useSearchParams } from 'react-router-dom'
import {
  Search, User, Users, LayoutGrid, ClipboardList, Tag, X, AlertCircle,
  Filter, ChevronDown, ChevronUp,
} from 'lucide-react'
import { FeaturedLayout } from '../../layouts'
import {
  Button, Input, Card, CardContent, Label, Checkbox,
  Alert, AlertDescription,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui'
import { YearMultiSelect } from '../../ui/year-multi-select'
import { desasignarPlan, quitarPromocion, getResumenBeca } from '../../../lib/supabase'
import { useConfirmDialog } from '../../../hooks/useConfirmDialog'
import { formatMoney } from '../../../utils/helpers'
import { useTemporadaActiva } from './useTemporadaActiva'
import TemporadaSelector from './TemporadaSelector'
import { useInscritosTemporada } from '../../../hooks/useInscritosTemporada'
import { FiltroInscritos, AvisoSinPadron } from '../FiltroInscritos'
import { UNIDADES, unidadesDeScope, scopeLabel } from '../../../constants/planScope'
import {
  filtrarJugadores, coberturaDeFamilia, coberturaDeGrupo, pasaCobertura, totalDelPlan, ESTADO_PLAN,
} from '../../../lib/planCobertura'
import { isVisiblePlayer } from '../../../lib/playerVisibility'
import useAsignacionesData from './asignaciones/useAsignacionesData'
import VistaJugadores from './asignaciones/VistaJugadores'
import VistaFamilias from './asignaciones/VistaFamilias'
import VistaGrupos from './asignaciones/VistaGrupos'
import AsignarPlanDialog from './asignaciones/AsignarPlanDialog'
import AsignarPromocionDialog from './asignaciones/AsignarPromocionDialog'
import QuitarBecaDialog from './asignaciones/QuitarBecaDialog'

// Radix no admite un SelectItem con valor vacio; estos son los centinelas de
// "sin elegir" para los Select controlados (plan y filtros).
const SIN_PLAN = '__sin_plan__'
const TODOS = '__todos__'

// Filtros detallados: estadoPlan/grupoId/deporte/rama/cobertura llegan como
// string vacio ('' = sin filtrar, lo que ya entiende filtrarJugadores);
// anios es array y conAdeudo booleano. `cobertura` solo aplica a familia/grupo.
const FILTROS_VACIOS = {
  estadoPlan: '', grupoId: '', deporte: '', rama: '', anios: [], conAdeudo: false, cobertura: '',
}

const ETIQUETA_UNIDAD = { jugador: 'Jugadores', familia: 'Familias', grupo: 'Grupos' }
const ICONO_UNIDAD = { jugador: User, familia: Users, grupo: LayoutGrid }

const PagosAsignaciones = () => {
  const [searchParams] = useSearchParams()
  // La temporada viaja junto con el plan (Planes -> "Asignar"): sin ella la
  // pantalla montaba en la temporada abierta y soltaba el plan por no ser de
  // ahi, en silencio.
  const { temporadaId, temporadas, setTemporadaId } = useTemporadaActiva(searchParams.get('temporada'))
  const inscritosEstado = useInscritosTemporada(temporadaId)
  const {
    asignaciones, planes: planesData, planesDe, jugadores, familias, grupos,
    inscritosPorGrupo, gruposDe, gruposNombresDe, deportesDe, adeudoDe, orgDeportes,
    promociones, promocionesDe,
    loading, error, reload,
  } = useAsignacionesData(temporadaId)
  const { confirm, alert, ConfirmDialog } = useConfirmDialog()

  const [planId, setPlanId] = useState(searchParams.get('plan') || '')
  const [unidad, setUnidad] = useState('jugador')
  const [seleccion, setSeleccion] = useState(new Set())
  const [busqueda, setBusqueda] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [filtros, setFiltros] = useState(FILTROS_VACIOS)
  // El dialogo lleva su propio par (unidad, ids): asi el aviso de faltantes
  // puede abrirlo para un grupo suelto sin borrar lo que el admin ya marco.
  const [dialogo, setDialogo] = useState(null)
  const [dialogoPromo, setDialogoPromo] = useState(null)
  const [becaDialog, setBecaDialog] = useState(null)

  const deporteOptions = useMemo(() => opcionesDeporte(orgDeportes), [orgDeportes])

  const plan = useMemo(() => planesData.find((p) => p.id === planId) || null, [planesData, planId])
  const unidadesPermitidas = useMemo(() => (plan ? unidadesDeScope(plan.scope) : UNIDADES), [plan])

  // El plan puede venir por URL desde Planes (junto con su temporada). Si el
  // admin cambia de temporada a mano, el plan elegido deja de existir aqui y se
  // suelta. Sin el guard de temporadaId esto se dispararia antes de que cargue
  // la lista; sin quitar el viejo `planesData.length &&`, una temporada con CERO
  // planes dejaba el planId pegado apuntando a nada.
  useEffect(() => {
    if (!temporadaId || loading || !planId) return
    if (!planesData.some((p) => p.id === planId)) setPlanId('')
  }, [planId, loading, planesData, temporadaId])

  // Cambiar de plan puede dejar la unidad actual prohibida; se corrige sola.
  useEffect(() => {
    if (!unidadesPermitidas.includes(unidad)) setUnidad(unidadesPermitidas[0] || 'jugador')
  }, [unidadesPermitidas, unidad])

  // La seleccion muere al cambiar de unidad o de temporada: arrastrar ids de
  // grupos a una lista de jugadores asignaria a quien nadie marco. Los
  // filtros tambien: son distintos por unidad (estado del plan vs cobertura)
  // y dejarlos puestos en silencio filtraria la vista nueva sin que se note.
  useEffect(() => { setSeleccion(new Set()); setFiltros(FILTROS_VACIOS) }, [unidad, temporadaId])

  // "Ya lo tiene" solo tiene sentido con un plan elegido (estadoPlanDeJugador
  // necesita el planId). Si se suelta el plan mientras el filtro sigue ahi, no
  // lo dejamos apuntando a un estado imposible ("nadie coincide" sin razon).
  useEffect(() => {
    if (!planId && filtros.estadoPlan === ESTADO_PLAN.CON_ESTE) {
      setFiltros((f) => ({ ...f, estadoPlan: '' }))
    }
  }, [planId, filtros.estadoPlan])

  const jugadoresDeFamilia = useMemo(() => {
    const map = {}
    familias.forEach((f) => {
      map[f.id] = inscritosEstado
        .filtrar((f.jugadores || []).filter(isVisiblePlayer))
        .map((j) => j.id)
    })
    return map
  }, [familias, inscritosEstado])

  const term = busqueda.trim().toLowerCase()

  // `visibles` es la lista ya filtrada: es la que marca "Seleccionar todos", para
  // que nunca se marque a alguien que no esta en pantalla.
  const visibles = useMemo(() => {
    if (unidad === 'jugador') {
      const base = inscritosEstado.filtrar(jugadores)
      return filtrarJugadores(base, filtros, {
        planId, planesDe, gruposDe, deportesDe, adeudoDe,
      }).filter((j) => !term
        || (j.nombre_completo || '').toLowerCase().includes(term)
        || String(j.numero ?? '').includes(term))
    }
    if (unidad === 'familia') {
      return familias
        .filter((f) => (jugadoresDeFamilia[f.id] || []).length > 0)
        .filter((f) => !term || (f.nombre || '').toLowerCase().includes(term))
        .filter((f) => pasaCobertura(filtros.cobertura,
          coberturaDeFamilia({ jugadoresDeFamilia: jugadoresDeFamilia[f.id] || [], planId, planesDe })))
    }
    return grupos
      .filter((g) => !term || (g.nombre_del_grupo || '').toLowerCase().includes(term))
      .filter((g) => pasaCobertura(filtros.cobertura,
        coberturaDeGrupo({ inscritosDelGrupo: inscritosPorGrupo[g.id] || [], planId, planesDe })))
  }, [unidad, jugadores, familias, grupos, jugadoresDeFamilia, term, filtros,
    planId, planesDe, gruposDe, deportesDe, adeudoDe, inscritosEstado, inscritosPorGrupo])

  const toggle = useCallback((id) => {
    setSeleccion((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  // Los filtros son distintos por unidad: en Jugadores es estado del
  // plan/grupo/deporte/rama/año/adeudo, en Familias y Grupos es cobertura.
  // El punto indicador y "Limpiar filtros" solo miran los que aplican a la
  // unidad actual.
  const hayFiltrosJugador = !!(filtros.estadoPlan || filtros.grupoId || filtros.deporte
    || filtros.rama || filtros.anios.length || filtros.conAdeudo)
  const hayFiltrosCobertura = !!filtros.cobertura
  const hayFiltros = unidad === 'jugador' ? hayFiltrosJugador : hayFiltrosCobertura
  const limpiarFiltros = () => setFiltros(FILTROS_VACIOS)

  // El vacio distingue "nada coincide con lo que pediste" (con salida: limpiar
  // filtros/busqueda) de "genuinamente no hay nada que asignar" (sin filtros
  // de por medio, hay que decir por que en vez de dejar la lista muda).
  const mensajeVacio = () => {
    if (hayFiltros || term) {
      if (unidad === 'grupo') return 'Ningún grupo coincide con los filtros.'
      if (unidad === 'familia') return 'Ninguna familia coincide con los filtros.'
      return 'Nadie coincide con los filtros.'
    }
    if (unidad === 'grupo') {
      return inscritosEstado.sinPadron
        ? 'Esta temporada aún no tiene inscritos, así que ningún grupo tiene todavía a quién asignarle un plan. Inscríbelos en Administración → Inscripciones.'
        : 'No hay grupos con jugadores inscritos en esta temporada.'
    }
    if (unidad === 'familia') return 'No hay familias con jugadores inscritos en esta temporada.'
    return 'No hay a quién asignar en esta temporada.'
  }

  const todosMarcados = visibles.length > 0 && visibles.every((x) => seleccion.has(x.id))
  const toggleTodos = () => {
    setSeleccion(todosMarcados ? new Set() : new Set(visibles.map((x) => x.id)))
  }

  // Aviso de faltantes: la unidad sigue siendo la del grupo/familia (asi el
  // selector del dialogo sigue ofreciendo planes de ese alcance), pero `limitarA`
  // acota la escritura a los faltantes que la vista ya calculo. Sin acotar,
  // resolverJugadores devolveria el grupo COMPLETO y reasignar a quien ya tenia
  // otro plan le cancela sus cargos pendientes: se le cambiaria el plan a gente
  // que el admin nunca marco.
  const asignarFaltantes = (id, faltantes) => setDialogo({ unidad, ids: [id], limitarA: faltantes })

  const onDone = async ({ jugadores: cuantos, cargos }) => {
    setDialogo(null)
    setSeleccion(new Set())
    await reload()
    await alert('Listo', `Plan asignado a ${cuantos} jugador(es). Se generaron ${cargos} cargo(s).`)
  }

  // El mensaje sale del resumen que devolvio el motor, no de una promesa escrita a
  // mano: decia "los ya emitidos no cambian" cuando ahora si cambian.
  const onDonePromo = async ({ jugadores: cuantos, promocion, resumen, becas, montoBeca }) => {
    setDialogoPromo(null)
    setSeleccion(new Set())
    await reload()
    const partes = []
    if (resumen?.cambios?.length) partes.push(`${resumen.cambios.length} cargo(s) bajaron de precio`)
    if (resumen?.historia?.length) partes.push(`${resumen.historia.length} con pagos no se modificaron`)
    if (resumen?.saldoGenerado > 0) partes.push(`${formatMoney(resumen.saldoGenerado)} en saldo a favor`)
    if (montoBeca > 0 && becas > 0) partes.push(`${becas} beca(s) de ${formatMoney(montoBeca)}`)
    await alert(
      'Listo',
      `"${promocion}" asignada a ${cuantos} jugador(es).`
      + (partes.length ? `\n\n${partes.join('. ')}.` : '\n\nNo hubo cargos por ajustar.'),
    )
  }

  // Una beca otorgo dinero: quitarla no es lo mismo que quitar una rebaja, asi que
  // se pregunta con los numeros delante y se deja elegir que pasa con lo aplicado.
  const handleQuitarPromocion = async (asignacionId, nombrePromo, nombreJugador) => {
    const { data: beca } = await getResumenBeca(asignacionId)
    if (beca?.esBeca) {
      setBecaDialog({ asignacionId, beca, jugador: nombreJugador })
      return
    }
    // Que pasa con el dinero, calculado antes de escribir por el mismo servicio
    // que va a hacerlo. Los cargos sin pagos vuelven a su precio de lista; los que
    // ya recibieron dinero no se encarecen -- eso reabriria una deuda saldada.
    const { data: previo } = await quitarPromocion(asignacionId, { dryRun: true })
    const vuelven = previo?.resumen?.cambios?.length || 0
    const intocados = previo?.resumen?.historia?.length || 0
    const detalle = [
      vuelven ? `${vuelven} cargo(s) volverán a su monto original.` : '',
      intocados ? `${intocados} cargo(s) con pagos no se modifican.` : '',
    ].filter(Boolean).join(' ')

    const ok = await confirm(
      'Quitar promoción',
      `Se le quitará "${nombrePromo}" a ${nombreJugador}.\n\n`
      + (detalle || 'No hay cargos por ajustar.')
      + '\n\n¿Continuar?',
      { destructive: true },
    )
    if (!ok) return
    await quitarPromocion(asignacionId)
    reload()
  }

  const confirmarQuitarBeca = async (opciones) => {
    await quitarPromocion(becaDialog.asignacionId, opciones)
    setBecaDialog(null)
    reload()
  }

  const handleDesasignar = async (asignacionId, nombrePlan, nombreJugador) => {
    const quien = nombreJugador ? ` a ${nombreJugador}` : ''
    const cual = nombrePlan ? ` "${nombrePlan}"` : ''
    const ok = await confirm(
      'Desasignar plan',
      `Se le quitará el plan${cual}${quien} y se cancelarán sus cargos pendientes de ese plan. Lo ya pagado no se toca. ¿Continuar?`,
      { destructive: true },
    )
    if (!ok) return
    await desasignarPlan(asignacionId)
    reload()
  }

  // Que planes tiene YA cada jugador. Antes esto era una segunda lista al pie de
  // la pantalla ("Planes ya asignados"): dos listas de las mismas personas,
  // filtradas por el mismo buscador, y costaba entender cual era cual. Ahora
  // alimenta la fila de cada jugador, que es donde la pregunta se hace.
  const planesDeJugador = useMemo(() => {
    const map = {}
    asignaciones.forEach((a) => {
      if (!map[a.jugador_id]) map[a.jugador_id] = []
      map[a.jugador_id].push({
        asignacionId: a.id,
        id: a.pagos_planes?.id,
        nombre: a.pagos_planes?.nombre || 'Plan',
      })
    })
    return map
  }, [asignaciones])

  return (
    <FeaturedLayout
      title="Asignaciones de planes"
      subtitle="Elige el plan y a quién se le asigna"
      refreshAction={reload}
    >
      {/* El selector en su propia fila: compartiendola no tiene limite de ancho y
          empuja el boton primario fuera de la pantalla. */}
      <div className="mb-3 flex min-w-0 items-center gap-3">
        <TemporadaSelector temporadas={temporadas} value={temporadaId} onChange={setTemporadaId} />
        <FiltroInscritos estado={inscritosEstado} id="as-ver-no-inscritos" />
      </div>
      {inscritosEstado.sinPadron && (
        <div className="mb-3"><AvisoSinPadron estado={inscritosEstado} /></div>
      )}
      {error && (
        <Alert variant="destructive" className="mb-3 py-2">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-xs">{error}</AlertDescription>
        </Alert>
      )}

      {/* Camino A: se elige el plan primero y la unidad se fija sola. */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Plan a asignar</Label>
          <div className="mt-1 flex min-w-0 items-center gap-2">
            <div className="min-w-0 flex-1">
              <Select
                value={planId || SIN_PLAN}
                onValueChange={(v) => setPlanId(v === SIN_PLAN ? '' : v)}
              >
                <SelectTrigger><SelectValue placeholder="Elegir plan" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={SIN_PLAN}>Aún no elijo plan</SelectItem>
                  {planesData.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {plan && (
              <Button
                variant="ghost"
                size="sm"
                className="shrink-0"
                onClick={() => setPlanId('')}
                aria-label="Quitar el plan elegido"
                title="Quitar el plan elegido"
              >
                <X className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">Quitar</span>
              </Button>
            )}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            {plan
              ? `${scopeLabel(plan.scope)} · ${formatMoney(totalDelPlan(plan))} · ${(plan.pagos_planes_parcialidades || []).length} parcialidad(es)`
              : 'También puedes marcar primero a quién y elegir el plan al confirmar.'}
          </p>
          {planesData.length === 0 && !loading && (
            <p className="mt-2 text-xs text-muted-foreground">
              Esta temporada no tiene planes. Créalos en la pestaña Planes.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Toolbar: unidad + busqueda, una sola fila que no se desborda en movil. */}
      <div className="mb-3 flex min-w-0 flex-nowrap items-center gap-2">
        <div className="flex shrink-0 items-center gap-1 rounded-lg bg-muted p-1">
          {UNIDADES.map((u) => {
            const permitida = unidadesPermitidas.includes(u)
            const Icono = ICONO_UNIDAD[u]
            return (
              <Button
                key={u}
                variant={unidad === u ? 'default' : 'ghost'}
                size="sm"
                disabled={!permitida}
                aria-label={ETIQUETA_UNIDAD[u]}
                title={permitida
                  ? ETIQUETA_UNIDAD[u]
                  : `"${plan?.nombre}" se asigna ${scopeLabel(plan?.scope).toLowerCase()}`}
                onClick={() => setUnidad(u)}
              >
                <Icono className="h-4 w-4 sm:mr-1.5" />
                <span className="hidden sm:inline">{ETIQUETA_UNIDAD[u]}</span>
              </Button>
            )
          })}
        </div>
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          className="relative h-10 w-10 shrink-0 p-0 sm:h-10 sm:w-auto sm:px-3"
          onClick={() => setShowFilters((s) => !s)}
          aria-label="Filtros"
          title="Filtros"
        >
          <Filter className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Filtros</span>
          {showFilters
            ? <ChevronUp className="ml-1 hidden h-4 w-4 sm:inline" />
            : <ChevronDown className="ml-1 hidden h-4 w-4 sm:inline" />}
          {hayFiltros && <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-primary" />}
        </Button>
        {/* La accion primaria vive en la toolbar, como en el resto de la app. El
            conteo va en el boton para no necesitar una barra aparte que lo diga. */}
        <Button
          className="h-10 shrink-0"
          disabled={seleccion.size === 0}
          onClick={() => setDialogo({ unidad, ids: [...seleccion] })}
          aria-label="Asignar plan a lo seleccionado"
          title={seleccion.size === 0
            ? 'Marca a quién le vas a asignar el plan'
            : `Asignar plan a ${seleccion.size} seleccionado(s)`}
        >
          <ClipboardList className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">
            Asignar plan{seleccion.size > 0 ? ` (${seleccion.size})` : ''}
          </span>
        </Button>
        {/* Secundaria: un plan cobra, una promocion solo descuenta lo que se
            cobre despues. Por eso el primario es el plan (ux-rules #10). */}
        <Button
          variant="outline"
          className="h-10 shrink-0"
          disabled={seleccion.size === 0}
          onClick={() => setDialogoPromo({ unidad, ids: [...seleccion] })}
          aria-label="Asignar promocion a lo seleccionado"
          title={seleccion.size === 0
            ? 'Marca a quién le vas a asignar la promoción'
            : `Asignar promoción a ${seleccion.size} seleccionado(s)`}
        >
          <Tag className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Asignar promoción</span>
        </Button>
      </div>

      {/* Solo cuando hay seleccion: en movil la etiqueta del boton se colapsa a
          icono, asi que el conteo tiene que decirse en algun lado. */}
      {seleccion.size > 0 && (
        <div className="mb-3 flex items-center gap-2 text-sm">
          <span className="font-medium">{seleccion.size} seleccionado(s)</span>
          <Button variant="ghost" size="sm" onClick={() => setSeleccion(new Set())}>Limpiar</Button>
        </div>
      )}

      {showFilters && (
        <Card className="mb-3">
          <CardContent className="p-4">
            {unidad === 'jugador' ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <div className="space-y-1">
                  <Label className="text-xs">Estado del plan</Label>
                  <Select
                    value={filtros.estadoPlan || TODOS}
                    onValueChange={(v) => setFiltros((f) => ({ ...f, estadoPlan: v === TODOS ? '' : v }))}
                  >
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={TODOS}>Todos</SelectItem>
                      <SelectItem value={ESTADO_PLAN.SIN_PLAN}>Sin plan</SelectItem>
                      <SelectItem value={ESTADO_PLAN.CON_ESTE} disabled={!planId}>
                        Ya lo tiene{!planId ? ' (elige un plan arriba)' : ''}
                      </SelectItem>
                      <SelectItem value={ESTADO_PLAN.CON_OTRO}>Con otro plan</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Grupo</Label>
                  <Select
                    value={filtros.grupoId || TODOS}
                    onValueChange={(v) => setFiltros((f) => ({ ...f, grupoId: v === TODOS ? '' : v }))}
                  >
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={TODOS}>Todos</SelectItem>
                      {grupos.map((g) => (
                        <SelectItem key={g.id} value={g.id}>{g.nombre_del_grupo}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Deporte</Label>
                  <Select
                    value={filtros.deporte || TODOS}
                    onValueChange={(v) => setFiltros((f) => ({ ...f, deporte: v === TODOS ? '' : v }))}
                  >
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={TODOS}>Todos</SelectItem>
                      {deporteOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Rama</Label>
                  <Select
                    value={filtros.rama || TODOS}
                    onValueChange={(v) => setFiltros((f) => ({ ...f, rama: v === TODOS ? '' : v }))}
                  >
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={TODOS}>Todos</SelectItem>
                      <SelectItem value="varonil">Varonil</SelectItem>
                      <SelectItem value="femenil">Femenil</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Año de nacimiento</Label>
                  <YearMultiSelect
                    selectedYears={filtros.anios}
                    onYearsChange={(anios) => setFiltros((f) => ({ ...f, anios }))}
                  />
                </div>
                <div className="flex items-end pb-1.5">
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <Checkbox
                      checked={filtros.conAdeudo}
                      onCheckedChange={(v) => setFiltros((f) => ({ ...f, conAdeudo: !!v }))}
                      aria-label="Con adeudo"
                    />
                    Con adeudo
                  </label>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <div className="space-y-1">
                  <Label className="text-xs">Cobertura</Label>
                  <Select
                    value={filtros.cobertura || TODOS}
                    onValueChange={(v) => setFiltros((f) => ({ ...f, cobertura: v === TODOS ? '' : v }))}
                  >
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={TODOS}>Todos</SelectItem>
                      <SelectItem value="completos">Completos</SelectItem>
                      <SelectItem value="faltantes">Con faltantes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            {hayFiltros && (
              <div className="mt-3 flex justify-end">
                <Button variant="ghost" size="sm" onClick={limpiarFiltros}>Limpiar filtros</Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {loading ? (
        <p className="py-8 text-center text-sm text-muted-foreground">Cargando...</p>
      ) : (
        <Card className="mb-4">
          <CardContent className="px-4 py-2">
            <label className="flex cursor-pointer items-center gap-2 border-b border-border py-2 text-xs text-muted-foreground">
              <Checkbox
                checked={todosMarcados}
                onCheckedChange={toggleTodos}
                disabled={visibles.length === 0}
                aria-label="Seleccionar todos los visibles"
              />
              Seleccionar todos ({visibles.length})
            </label>

            {visibles.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-sm text-muted-foreground">{mensajeVacio()}</p>
                {(hayFiltros || term) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2"
                    onClick={() => { limpiarFiltros(); setBusqueda('') }}
                  >
                    Limpiar filtros
                  </Button>
                )}
              </div>
            ) : (
              <>
                {unidad === 'jugador' && (
                  <VistaJugadores
                    jugadores={visibles}
                    gruposNombresDe={gruposNombresDe}
                    planId={planId}
                    planesDeJugador={planesDeJugador}
                    onDesasignar={handleDesasignar}
                    promocionesDeJugador={promocionesDe}
                    onQuitarPromocion={handleQuitarPromocion}
                    adeudoDe={adeudoDe}
                    seleccion={seleccion}
                    onToggle={toggle}
                  />
                )}
                {unidad === 'familia' && (
                  <VistaFamilias
                    familias={visibles}
                    jugadoresDeFamilia={jugadoresDeFamilia}
                    planId={planId}
                    planesDe={planesDe}
                    seleccion={seleccion}
                    onToggle={toggle}
                    onAsignarFaltantes={asignarFaltantes}
                  />
                )}
                {unidad === 'grupo' && (
                  <VistaGrupos
                    grupos={visibles}
                    inscritosPorGrupo={inscritosPorGrupo}
                    planId={planId}
                    planesDe={planesDe}
                    seleccion={seleccion}
                    onToggle={toggle}
                    onAsignarFaltantes={asignarFaltantes}
                  />
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      <QuitarBecaDialog
        open={!!becaDialog}
        onOpenChange={(v) => { if (!v) setBecaDialog(null) }}
        beca={becaDialog?.beca}
        jugador={becaDialog?.jugador}
        onConfirm={confirmarQuitarBeca}
      />

      <AsignarPromocionDialog
        open={!!dialogoPromo}
        onOpenChange={(v) => { if (!v) setDialogoPromo(null) }}
        unidad={dialogoPromo?.unidad || unidad}
        ids={dialogoPromo?.ids || []}
        temporadaId={temporadaId}
        promociones={promociones}
        onDone={onDonePromo}
      />
      <AsignarPlanDialog
        open={!!dialogo}
        onOpenChange={(v) => { if (!v) setDialogo(null) }}
        unidad={dialogo?.unidad || unidad}
        ids={dialogo?.ids || []}
        limitarA={dialogo?.limitarA || null}
        temporadaId={temporadaId}
        planes={planesData}
        planId={planId}
        planesDe={planesDe}
        onDone={onDone}
      />
      <ConfirmDialog />
    </FeaturedLayout>
  )
}

export default PagosAsignaciones
